import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  styled,
} from '@material-ui/core';
import { datadogRum } from '@datadog/browser-rum';

import { SUPABASE_URL } from '@/utils/environmentUtils';
import { logError } from '@/utils/logError';

const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
});

// Parameters that contain the destination URL (not forwarded)
// When iterating through URL params, these will be excluded
const URL_PARAMS = ['_p', '_fallback_redirect'];

/**
 * Where this page is allowed to send a browser.
 *
 * `_p` and `_fallback_redirect` are attacker-addressable — this is a public URL — and they
 * reach `window.location.href`. Without an origin check, a link of the form
 * `/s?_p=https://evil.example` turns a Big Health hostname into an open redirect, and
 * `new URL('javascript:…')` parses and keeps that protocol, so the same sink accepts a script
 * URL.
 *
 * The only legitimate destination is the Supabase verify URL the auth service put in the
 * email, plus our own origin for in-app hops. Anything else goes to `/`.
 */
const isAllowedDestination = (url: URL): boolean => {
  if (url.protocol !== 'https:') {
    return false;
  }

  // `SUPABASE_URL` resolves through `envSwitch`, which indexes its options by
  // REACT_APP_DEPLOY_ENV with no fallback — so an unset or unexpected value yields undefined.
  // Guarded rather than parsed blind: `new URL(undefined)` throws, the outer catch turns that
  // into a redirect to `/`, and every visitor to this page loses their destination. This page
  // never read SUPABASE_URL before, so that misconfiguration used to be harmless here.
  let supabaseOrigin: string | null = null;
  if (SUPABASE_URL) {
    try {
      supabaseOrigin = new URL(SUPABASE_URL).origin;
    } catch {
      supabaseOrigin = null;
    }
  }
  if (!supabaseOrigin) {
    logError('[SingularLanding]', 'SUPABASE_URL is not configured; verify destinations will be refused', undefined, {
      level: 'warning',
      extra: { deployEnv: process.env.REACT_APP_DEPLOY_ENV ?? 'unset' },
    });
  }

  // Our own origin is allowed so an in-app hop still works — that is also the arm the Sleepio
  // Cortex flow relies on, since its `_p` is a CAP URL rather than a Supabase one. Filtered
  // rather than assumed, because a test environment can replace `window.location` with a
  // plain object.
  const allowedOrigins = [supabaseOrigin, window.location?.origin].filter(Boolean);
  return allowedOrigins.includes(url.origin);
};

const SingularLanding: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Get destination URL: _p takes priority over _fallback_redirect
    const destinationParam = searchParams.get('_p') || searchParams.get('_fallback_redirect');

    datadogRum.addAction('[SingularLanding] page loaded', {
      hasP: Boolean(searchParams.get('_p')),
      hasFallback: Boolean(searchParams.get('_fallback_redirect')),
      hasDestination: Boolean(destinationParam),
    });

    const redirect = (url: string, outcome: string) => {
      datadogRum.addAction('[SingularLanding] redirecting', { outcome });
      // Give Singular SDK time to process the link before redirecting
      const redirectTimer = setTimeout(() => {
        window.location.href = url;
      }, 500);

      return () => clearTimeout(redirectTimer);
    };

    if (destinationParam) {
      try {
        // `searchParams.get()` has ALREADY decoded this value once. Decoding it a second
        // time is what broke Sleepio login (PAT-1286): the destination is a Supabase verify
        // URL whose own `redirect_to` carries the app's return address as a nested param,
        // separated by an ENCODED ampersand. A second decode turns that `%26` into a real
        // `&`, which promotes the nested param to a sibling of `redirect_to` — so Supabase
        // redirected to the callback without the app's address, and a returning member
        // ended up back on the sign-up screen having done everything right.
        //
        // Parse the value as-is. Only fall back to decoding when it is not yet a URL, so a
        // double-encoded link from another sender still works.
        let destinationUrl: URL;
        try {
          destinationUrl = new URL(destinationParam);
        } catch {
          destinationUrl = new URL(decodeURIComponent(destinationParam));
        }

        if (!isAllowedDestination(destinationUrl)) {
          // Not an error the member caused, and not one they can act on, so it is logged and
          // they go to the root rather than being shown anything.
          // Origin only — the rest of the URL carries a one-time verify token. The origin is
          // what a reader needs: a cross-environment link refused in QA and a genuinely
          // hostile destination look identical without it.
          logError(
            '[SingularLanding]',
            'refused a destination outside the allowed origins',
            undefined,
            {
              level: 'warning',
              extra: {
                protocol: destinationUrl.protocol,
                refusedOrigin: destinationUrl.origin,
                deployEnv: process.env.REACT_APP_DEPLOY_ENV ?? 'unset',
              },
            },
          );
          return redirect('/', 'refused-destination');
        }

        // Append any extra params (excluding _p and _fallback_redirect)
        searchParams.forEach((value, key) => {
          if (!URL_PARAMS.includes(key) && value) {
            destinationUrl.searchParams.set(key, value);
          }
        });

        return redirect(destinationUrl.toString(), 'destination');
      } catch {
        // Invalid URL, fallback to root
        return redirect('/', 'error_invalid_url');
      }
    }

    // No destination provided, fallback to root
    return redirect('/', 'no_destination');
  }, [location.search]);

  // Show loading indicator while processing
  // This might work so fast that the user may never see it,
  // but it's here for the JIC.
  return (
    <CenteredContainer maxWidth="sm">
      <CircularProgress size="6rem" thickness={3} />
    </CenteredContainer>
  );
};

export default SingularLanding;
